/**
 * 分包记录面板组件
 */
import React, { useState, useEffect } from 'react';
import { Table, Spin, Empty, message, Tag, Card, Col, Row, Statistic, Progress, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import api from '../../../services/api';
import { formatMoney } from '../../../utils/budgetUtils';

const { Text } = Typography;

interface SubcontractPanelProps {
  projectId: string;
  isMobile?: boolean;
  active?: boolean;
}

interface SubcontractRecord {
  _id: string;
  costDate: string;
  contractName: string;
  contractAmount: number;
  paidAmount: number;
  status: 'active' | 'completed' | 'cancelled';
  description?: string;
}

interface SubcontractResponse {
  success: boolean;
  data?: SubcontractRecord[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface ProjectSubcontractOperatingStats {
  projectId: string;
  contractAmount: number;
  settlementAmount: number;
  settledAmount: number;
  paidAmount: number;
  unpaidAmount: number;
  paymentProgress: number;
  recordCount: number;
  byStatus: {
    active: number;
    completed: number;
    cancelled: number;
  };
  byConstructionType: Array<{
    constructionType: string;
    contractAmount: number;
    settlementAmount: number;
    settledAmount: number;
    paidAmount: number;
    recordCount: number;
  }>;
}

interface OperatingStatsResponse {
  success: boolean;
  data?: ProjectSubcontractOperatingStats;
}

const getStatusTag = (status: SubcontractRecord['status']) => {
  if (status === 'completed') return <Tag color="green">已完成</Tag>;
  if (status === 'cancelled') return <Tag color="red">已取消</Tag>;
  return <Tag color="blue">进行中</Tag>;
};

const statusCardStyle = {
  borderRadius: 12,
  background: '#fafbfc',
  border: '1px solid rgba(16, 23, 29, 0.06)',
} as const;

const SubcontractPanel: React.FC<SubcontractPanelProps> = ({
  projectId,
  isMobile = false,
  active = false
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SubcontractRecord[]>([]);
  const [operatingStats, setOperatingStats] = useState<ProjectSubcontractOperatingStats | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: isMobile ? 5 : 10,
    total: 0
  });
  const [loaded, setLoaded] = useState(false);

  const fetchData = async (page = 1, pageSize = isMobile ? 5 : 10) => {
    if (!projectId) return;

    setLoading(true);
    try {
      const [response, operatingStatsResponse] = await Promise.all([
        api.get('/subcontract-costs', {
          params: {
            projectId,
            page,
            limit: pageSize
          }
        }) as Promise<SubcontractResponse>,
        api.get(`/subcontract-costs/operating-stats/${projectId}`) as Promise<OperatingStatsResponse>,
      ]);

      if (response.success && response.data) {
        setData(response.data);
        setPagination({
          current: response.pagination?.page || page,
          pageSize: response.pagination?.limit || pageSize,
          total: response.pagination?.total || 0
        });
      }

      if (operatingStatsResponse.success && operatingStatsResponse.data) {
        setOperatingStats(operatingStatsResponse.data);
      }
    } catch (error) {
      console.error('获取分包记录失败:', error);
      message.error('获取分包记录失败');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (active && !loaded) {
      fetchData();
    }
  }, [active, loaded, projectId]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return dateStr.split('T')[0];
  };

  const columns: ColumnsType<SubcontractRecord> = isMobile ? [
    {
      title: '合同/日期',
      key: 'info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.contractName}</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {formatDate(record.costDate)} · {getStatusTag(record.status)}
          </div>
          {record.description && (
            <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
              {record.description}
            </div>
          )}
        </div>
      )
    },
    {
      title: '金额',
      key: 'amount',
      width: 100,
      align: 'right',
      render: (_, record) => {
        const amount = record.paidAmount > 0 ? record.paidAmount : record.contractAmount;
        return formatMoney(amount, false);
      }
    }
  ] : [
    {
      title: '日期',
      dataIndex: 'costDate',
      key: 'costDate',
      width: 120,
      render: (val: string) => formatDate(val)
    },
    {
      title: '合同名称',
      dataIndex: 'contractName',
      key: 'contractName',
      width: 180,
      ellipsis: true
    },
    {
      title: '合同状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (val: SubcontractRecord['status']) => getStatusTag(val)
    },
    {
      title: '金额',
      key: 'actualAmount',
      width: 130,
      align: 'right',
      render: (_, record) => {
        const amount = record.paidAmount > 0 ? record.paidAmount : record.contractAmount;
        return formatMoney(amount, false);
      }
    },
    {
      title: '备注',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (val: string) => val || '-'
    }
  ];

  const renderStats = () => {
    if (!operatingStats) return null;

    const statusItems = [
      { label: '进行中', value: operatingStats.byStatus?.active ?? 0, color: '#1677ff' },
      { label: '已完成', value: operatingStats.byStatus?.completed ?? 0, color: '#52c41a' },
      { label: '已取消', value: operatingStats.byStatus?.cancelled ?? 0, color: '#ff4d4f' },
    ];

    return (
      <div style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12} xl={6}>
            <Card size="small" style={statusCardStyle} bodyStyle={{ padding: 16 }}>
              <Statistic title="合同金额" value={operatingStats.contractAmount} precision={2} formatter={(value) => formatMoney(Number(value || 0), false)} />
            </Card>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Card size="small" style={statusCardStyle} bodyStyle={{ padding: 16 }}>
              <Statistic title="应结算金额" value={operatingStats.settlementAmount} precision={2} formatter={(value) => formatMoney(Number(value || 0), false)} />
            </Card>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Card size="small" style={statusCardStyle} bodyStyle={{ padding: 16 }}>
              <Statistic title="已支付金额" value={operatingStats.paidAmount} precision={2} formatter={(value) => formatMoney(Number(value || 0), false)} />
            </Card>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Card size="small" style={statusCardStyle} bodyStyle={{ padding: 16 }}>
              <Statistic title="未支付金额" value={operatingStats.unpaidAmount} precision={2} formatter={(value) => formatMoney(Number(value || 0), false)} />
            </Card>
          </Col>
        </Row>

        <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
          <Col xs={24} lg={10}>
            <Card size="small" style={statusCardStyle} bodyStyle={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong>支付进度</Text>
                <Text type="secondary">{operatingStats.paymentProgress.toFixed(1)}%</Text>
              </div>
              <Progress
                percent={Math.min(operatingStats.paymentProgress, 100)}
                size={isMobile ? 'small' : 'default'}
                status={operatingStats.paymentProgress >= 100 ? 'success' : 'active'}
              />
              {operatingStats.paymentProgress > 100 && (
                <Text type="warning" style={{ fontSize: 12 }}>
                  已付金额高于已结算金额，请复核结算与付款记录。
                </Text>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={14}>
            <Card size="small" style={statusCardStyle} bodyStyle={{ padding: 16 }}>
              <div style={{ marginBottom: 10 }}>
                <Text strong>合同状态分布</Text>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  共 {operatingStats.recordCount} 条
                </Text>
              </div>
              <Row gutter={[8, 8]}>
                {statusItems.map(item => (
                  <Col span={8} key={item.label}>
                    <div style={{ padding: '10px 12px', borderRadius: 10, background: '#fff', border: `1px solid ${item.color}22` }}>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{item.label}</div>
                      <div style={{ fontSize: 20, fontWeight: 600, color: item.color }}>{item.value}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  if (!loaded && !active) {
    return null;
  }

  if (loading && !data.length) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <Spin tip="加载中..." />
      </div>
    );
  }

  if (!data.length) {
    return (
      <>
        {renderStats()}
        <Empty description="暂无分包记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </>
    );
  }

  return (
    <>
      {renderStats()}
      <Table
        columns={columns}
        dataSource={data}
        rowKey="_id"
        loading={loading}
        size="small"
        pagination={{
          ...pagination,
          size: 'small',
          showSizeChanger: false,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => fetchData(page, pageSize)
        }}
      />
    </>
  );
};

export default SubcontractPanel;
